// Material 3 Top App Bar & Navigation Component
// Features Material 3 Tonal Navigation Pill Buttons for clean, overlap-free navigation
(function () {
  const { AppBar, Toolbar, Typography, Container, Box } = MaterialUI;
  const { useState, useRef, useLayoutEffect } = React;

  // Destinations shared by the top bar (wide screens) and the NavigationBar (compact screens)
  // Tab ids: 0 apps, 1 About me, 2 admin, 'p:<key>' for every other portfolio page. A page with pages in its dropdown has `children`
  // (the page itself comes first when the dropdown opens).
  const pageTab = key => (key === 'about' ? 1 : `p:${key}`);
  // Where guests arrive: the Home landing page (portfolio-home.js), or About me when Home is deleted or hidden.
  const landingTab = (pages = []) => (pages.some(p => p.key === 'home') ? 'p:home' : 1);
  // What the account can see and manage (`user.permissions`, from the server's permissions.js). A user saved before
  // permissions existed falls back to its role until /api/auth/me refreshes it.
  function can(user, key) {
    if (!user) return false;
    if (Array.isArray(user.permissions)) return user.permissions.includes(key);
    if (user.role === 'admin') return true;
    return key === 'portfolio' ? user.role !== 'guest' : key.startsWith('app.');
  }
  // The Admin Console sections, each behind its own permission (AdminView.js)
  const NAV_FONT = "var(--wb-font-nav, 'Google Sans', 'Google Sans Text', Roboto, sans-serif)";
  const ADMIN_SECTIONS = ['admin.health', 'admin.infra', 'admin.visitors', 'admin.components', 'admin.settings', 'admin.users', 'admin.trash'];
  const canAdminConsole = user => ADMIN_SECTIONS.some(k => can(user, k));
  // "Admin", or "User (custom)" when the account's access was changed from its role's preset
  const accessLabel = user => `${window.Components.capitalize(user.role)}${user.customAccess ? ' (custom)' : ''}`;
  function getNavItems(user, pages = []) {
    const entry = p => ({ id: pageTab(p.key), label: p.published ? p.title : `${p.title} (hidden)`, icon: p.icon });
    const tops = pages.filter(p => !p.parent).map(p => {
      const kids = pages.filter(c => c.parent === p.key).map(entry);
      return kids.length ? { ...entry(p), children: kids } : entry(p);
    });
    // Accounts without the portfolio permission (the Guest preset) only get My Apps
    return [
      { id: 0, label: user ? 'My Apps' : 'Explore Apps', icon: 'apps' },
      ...(user && !can(user, 'portfolio') ? [] : (tops.length ? tops : [{ id: 1, label: 'About me', icon: 'badge' }])),
      ...(canAdminConsole(user)
        ? [{ id: 2, label: 'Admin Console', icon: 'admin_panel_settings' }]
        : [])
    ];
  }

  function Navbar({
    user,
    currentTab,
    onTabChange,
    onOpenAuth,
    onUserMenuOpen,
    isUserMenuOpen,
    themeMode = 'dark',
    themeSeed = 'purple',
    onToggleTheme,
    onOpenThemePicker,
    pages = [],
    pagesReady = true, // false while the page list is loading (first visit): the links stay hidden, so they appear once instead of reflowing
    onOpenNavMenu,
    onManagePages
  }) {
    const { GoogleIcon, IconTile, Button, IconButton, UserAvatar, Tooltip } = window.Components || {};
    const theme = MaterialUI.useTheme();
    const isDark = themeMode ? (themeMode === 'auto' ? (window.Components?.resolveM3Mode?.('auto') === 'dark') : themeMode === 'dark') : theme?.palette?.mode !== 'light';
    const colors = theme?.palette?.m3 || (isDark ? window.Components?.m3DarkColors : window.Components?.m3LightColors) || {};

    const handleBrandClick = () => {
      onTabChange(user ? 0 : landingTab(pages));
    };

    const handleNavClick = (id, item, el) => {
      if (item && item.children && onOpenNavMenu) { onOpenNavMenu(item, el); return; }
      if (!user && id === 0) {
        // Guest clicked Apps -> prompt sign in
        onOpenAuth();
        return;
      }
      onTabChange(id);
    };

    const navItems = getNavItems(user, pages);

    // Sections (every page except My Apps and Admin Console) move behind a hamburger menu when they do not fit the top bar.
    const MENU = '__menu';
    const withMenu = navItems.map(i => (i.id === 0 || i.id === 2 ? i : { ...i, inMenu: true }));
    const menuEntries = withMenu.filter(i => i.inMenu).flatMap(i => [{ ...i, children: undefined }, ...(i.children || [])]);
    const [collapsed, setCollapsed] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const rowRef = useRef(null), brandRef = useRef(null), trailRef = useRef(null), measureRef = useRef(null);
    const sig = navItems.map(i => i.label + (i.children ? i.children.length : '')).join('|') + (onManagePages ? '+' : '') + (user ? 'u' : 'g');
    useLayoutEffect(() => {
      const fit = () => {
        const row = rowRef.current, m = measureRef.current;
        if (!row || !m || !brandRef.current || !trailRef.current) return;
        // the links sit in the middle column of a 1fr auto 1fr grid, so both sides count as wide as the wider one
        const avail = row.clientWidth - 2 * Math.max(brandRef.current.offsetWidth, trailRef.current.offsetWidth) - 48;
        setCollapsed(m.offsetWidth > avail);
      };
      fit();
      const ro = new ResizeObserver(fit);
      ro.observe(rowRef.current);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
      return () => ro.disconnect();
    }, [sig]);

    const renderLink = (item, tab) => {
      const owns = id => id === item.id || (item.children || []).some(c => c.id === id);
      // Guests on tab 0 see the landing page, not the apps launchpad, so only that page is active then
      const shown = !user && currentTab === 0 ? landingTab(pages) : currentTab;
                  const isActive = item.isMenu ? menuEntries.some(e => e.id === shown) : owns(shown);
  
                  return (
                    <Button
                      key={item.id}
                      variant={isActive ? 'tonal' : 'text'}
                      onClick={e => (item.isMenu ? setMenuAnchor(e.currentTarget) : handleNavClick(item.id, item, e.currentTarget))}
                      aria-haspopup={item.isMenu ? 'menu' : undefined}
                    tabIndex={tab === false ? -1 : undefined}
                      icon={<GoogleIcon name={item.icon} size={20} filled={isActive} />}
                      sx={{
                        borderRadius: 'var(--m3-shape-full)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        px: 2.2,
                        py: 0.8,
                        minHeight: 40,
                        fontFamily: NAV_FONT, // Admin › Site settings: the navbar links' font
                        fontSynthesis: 'none',
                        fontSize: '0.88rem',
                        fontWeight: isActive ? 600 : 500,
                        letterSpacing: '0.1px',
                        color: isActive ? (colors.onSecondaryContainer || 'var(--m3-on-secondary-container)') : 'text.secondary', // the container's own text colour: "primary" on it fails at high contrast, where Material swaps which side is light
                        backgroundColor: isActive ? (colors.secondaryContainer || 'color-mix(in srgb, var(--m3-primary) 16%, transparent)') : 'transparent',
                        border: isActive ? `1px solid ${colors.outlineVariant || 'color-mix(in srgb, var(--m3-primary) 28%, transparent)'}` : '1px solid transparent',
                        transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
                        '&:hover': {
                          backgroundColor: isActive
                            ? (colors.secondaryContainer || 'color-mix(in srgb, var(--m3-on-surface) 12%, transparent)')
                            : 'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)',
                          color: 'text.primary'
                        }
                      }}
                    >
                      {item.label}
                      {(item.children || item.isMenu) && <GoogleIcon name="arrow_drop_down" size={20} style={{ marginRight: -6 }} />}
                    </Button>
                  );
    };

    const navBg = 'color-mix(in srgb, var(--m3-surface-container) 88%, transparent)';

    return (
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: navBg,
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${colors.outlineVariant || ('var(--m3-outline-variant)')}`,
          color: 'text.primary',
          zIndex: 1100,
          transition: 'background-color 0.25s cubic-bezier(0.2, 0, 0, 1), border-color 0.25s cubic-bezier(0.2, 0, 0, 1)'
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 } }}>
          <Toolbar ref={rowRef} disableGutters sx={{ position: 'relative', display: 'grid', gridTemplateColumns: { xs: 'auto auto', md: '1fr auto 1fr' }, alignItems: 'center', justifyContent: 'space-between', minHeight: { xs: 60, md: 68 } }}>
            {/* Leading: Brand Logo & Interactive Title */}
            <Box
              ref={brandRef}
              component="button"
              type="button"
              onClick={handleBrandClick}
              aria-label="WhimsicalBox home"
              sx={{
                justifySelf: 'start',
                font: 'inherit',
                border: 0,
                background: 'none',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                userSelect: 'none',
                minHeight: 44,
                px: 1,
                borderRadius: '16px',
                transition: 'background-color 0.2s cubic-bezier(0.2, 0, 0, 1)',
                '&:hover': {
                  backgroundColor:'color-mix(in srgb, var(--m3-on-surface) 6%, transparent)'}
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  component="span"
                  sx={{
                    display: 'block',
                    // The site name in the font picked in Admin › Site settings (Whimsical Pixel by default), at 20px where every
                    // pixel-font pixel is two screen pixels
                    fontFamily: "var(--wb-font-brand, 'Whimsical Pixel', monospace)",
                    fontWeight: 400,
                    fontSynthesis: 'none',
                    lineHeight: 1,
                    letterSpacing: 0,
                    color: 'text.primary',
                    fontSize: '20px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  WhimsicalBox
                </Typography>
              </Box>
            </Box>

            {/* Hidden copy of the full link row: its width decides whether the sections fit or move behind the hamburger */}
            <Box ref={measureRef} aria-hidden="true" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, position: 'absolute', left: 0, top: 0, visibility: 'hidden', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
              {navItems.map(item => renderLink(item, false))}
              {can(user, 'edit.pages') && onManagePages && <Box sx={{ width: 36 }} />}
            </Box>

            {/* Center: Desktop Navigation (Material 3 Tonal Pill Buttons) */}
            <Box component="nav" aria-label="Primary" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, visibility: pagesReady ? 'visible' : 'hidden' }}>
              {(collapsed && menuEntries.length > 0
                ? [withMenu[0], { id: MENU, label: 'Menu', icon: 'menu', isMenu: true }, ...withMenu.slice(1).filter(i => !i.inMenu)]
                : navItems).map(item => renderLink(item))}
              {can(user, 'edit.pages') && onManagePages && (
                <Tooltip title="Manage top bar pages">
                  <IconButton onClick={onManagePages} aria-label="Manage top bar pages" size="small"><GoogleIcon name="edit_square" size={20} /></IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Trailing: Theme Palette, Theme Toggle & (Avatar OR Sign In) */}
            <Box
              ref={trailRef}
              sx={{
                justifySelf: 'end',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.8, sm: 1.2 },
                flexShrink: 0
              }}
            >
              {/* Signed-in users get theme and mode controls in the account menu; guests have no menu, so keep them here */}
              {!user && <Tooltip title="Material 3 Theme & Colors">
                <IconButton
                  onClick={onOpenThemePicker}
                  aria-label="Material 3 Theme & Colors"
                  size="medium"
                  sx={{
                    width: 42,
                    height: 42,
                    minWidth: 42,
                    minHeight: 42,
                    flexShrink: 0,
                    p: 0,
                    color: colors.primary || 'var(--m3-primary)',
                    borderRadius: '50%',
                    backgroundColor:'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)',
                    border: `1px solid ${colors.outlineVariant || 'color-mix(in srgb, var(--m3-primary) 25%, transparent)'}`,
                    transition: 'all 0.25s cubic-bezier(0.2, 0, 0, 1)',
                    '&:hover': {
                      transform: 'rotate(15deg) scale(1.08)',
                      backgroundColor: colors.primaryContainer || 'color-mix(in srgb, var(--m3-primary) 18%, transparent)',
                      borderColor: colors.primary || 'var(--m3-primary)'
                    }
                  }}
                >
                  <GoogleIcon name="palette" size={22} />
                </IconButton>
              </Tooltip>}

              {/* Material 3 Dark / Light Mode Toggle Button */}
              {!user && <Tooltip title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                <IconButton
                  onClick={onToggleTheme}
                  aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                  size="medium"
                  sx={{
                    width: 42,
                    height: 42,
                    minWidth: 42,
                    minHeight: 42,
                    flexShrink: 0,
                    p: 0,
                    color: colors.primary || 'var(--m3-primary)',
                    borderRadius: '50%',
                    backgroundColor:'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)',
                    border: `1px solid ${colors.outlineVariant || 'color-mix(in srgb, var(--m3-primary) 25%, transparent)'}`,
                    transition: 'all 0.25s cubic-bezier(0.2, 0, 0, 1)',
                    '&:hover': {
                      transform: 'rotate(24deg) scale(1.08)',
                      backgroundColor: colors.primaryContainer || 'color-mix(in srgb, var(--m3-primary) 18%, transparent)',
                      borderColor: colors.primary || 'var(--m3-primary)'
                    }
                  }}
                >
                  <GoogleIcon
                    name={isDark ? 'light_mode' : 'dark_mode'}
                    size={22}
                    filled={isDark}
                  />
                </IconButton>
              </Tooltip>}

              {user ? (
                /* Signed-in user: Avatar & Context Menu Trigger */
                <Tooltip title={`${user.displayName || user.username} (${accessLabel(user)}) • Account & Settings`}>
                  <IconButton
                    onClick={onUserMenuOpen}
                    aria-haspopup="true"
                    aria-expanded={isUserMenuOpen ? 'true' : undefined}
                    sx={{
                      width: 44,
                      height: 44,
                      minWidth: 44,
                      minHeight: 44,
                      flexShrink: 0,
                      p: 0,
                      transition: 'box-shadow 0.2s cubic-bezier(0.2, 0, 0, 1)',
                      boxShadow: isUserMenuOpen ? '0 0 0 3px var(--m3-primary)' : 'none',
                      '&:hover': { bgcolor: 'color-mix(in srgb, var(--m3-on-surface) 8%, transparent)' }
                    }}
                  >
                    <UserAvatar
                      user={user}
                      size={38}
                    />
                  </IconButton>
                </Tooltip>
              ) : (
                /* Signed-out / Guest: Sign In Button directly next to the Theme Switcher */
                <Button
                  variant="filled"
                  color="primary"
                  icon={<GoogleIcon name="login" size={20} />}
                  onClick={onOpenAuth}
                  aria-label="Sign in"
                  sx={{
                    flexShrink: 0,
                    px: { xs: 1.5, sm: 2.75 },
                    '& .m3-signin-label': { display: { xs: 'none', sm: 'inline' } },
                    height: { xs: 44, sm: 40 },
                    fontSize: '0.88rem',
                    fontWeight: 600
                  }}
                >
                  <Box component="span" className="m3-signin-label">Sign In</Box>
                </Button>
              )}
            </Box>
          </Toolbar>
        </Box>
        <window.Components.NavMenu items={withMenu.filter(i => i.inMenu)} value={!user && currentTab === 0 ? 1 : currentTab} onSelect={onTabChange}
          anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} minWidth={240} PaperProps={{ sx: { maxHeight: 'min(70dvh, 420px)' } }} />
      </AppBar>
    );
  }

  window.Components = window.Components || {};
  window.Components.Navbar = Navbar;
  window.Components.getNavItems = getNavItems;
  window.Components.landingTab = landingTab;
  window.Components.can = can;
  window.Components.canAdminConsole = canAdminConsole;
  window.Components.accessLabel = accessLabel;
})();
